import { useTranslation } from 'react-i18next';

import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { RECEIVE_GROUP_BY_DATE, RECEIVE_MOVEMENT_LABEL_KEYS } from '@/entities/receive';

import type { ReceiveMovementSection as ReceiveMovementSectionData } from '../model/use-receive';
import type { ReceiveColumnHandlers } from '../model/columns';
import { ReceiveCategoryCard } from './receive-category-card';

interface ReceiveMovementSectionProps {
  section: ReceiveMovementSectionData;
  projectId?: number;
  siteId?: number;
  filterKey: string;
  dataVersion: number;
  columnHandlers: ReceiveColumnHandlers;
  canExport: boolean;
  canReceive: boolean;
  onExport: () => void;
  isExporting: boolean;
}

export function ReceiveMovementSection({
  section,
  projectId,
  siteId,
  filterKey,
  dataVersion,
  columnHandlers,
  canExport,
  canReceive,
  onExport,
  isExporting
}: ReceiveMovementSectionProps) {
  const { t } = useTranslation();
  const cardsWithData = section.cards.filter((card) => card.locations.length > 0);

  return (
    <AccordionItem value={section.movementType}>
      <AccordionTrigger>
        <span className="flex items-center gap-2">
          <span className="text-xl font-semibold text-foreground">
            {t(RECEIVE_MOVEMENT_LABEL_KEYS[section.movementType])}
          </span>
          <Badge variant="secondary">
            {t('receive.dashboard.subtitle', '{{count}} Consignments', { count: section.consignmentCount })}
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent className="space-y-6 pt-2">
        {cardsWithData.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('receive.dashboard.empty.section', 'No shipments in this section.')}
          </p>
        ) : (
          cardsWithData.map((card) => (
            <ReceiveCategoryCard
              key={card.locationType}
              movementType={section.movementType}
              locationType={card.locationType}
              locations={card.locations}
              groupByDate={RECEIVE_GROUP_BY_DATE[section.movementType]}
              projectId={projectId}
              siteId={siteId}
              filterKey={filterKey}
              dataVersion={dataVersion}
              columnHandlers={columnHandlers}
              canExport={canExport}
              canReceive={canReceive}
              onExport={onExport}
              isExporting={isExporting}
            />
          ))
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
